/**
 * Oath S45.5 — <oath-mobile-menu> web component.
 *
 * GENUINE GAP (verified S48): full-screen overlay menu (hero promo + 2×2 series
 * cards + footer CTA) is not the native side-slide header-drawer; the custom
 * overlay is justified. It REUSES the native hamburger trigger (intercepts it).
 *
 * S48 HARDENING: refactored from raw `extends HTMLElement` (IIFE) to extend
 * Horizon's `@theme/component` Component (ES module). Own-DOM handles are now
 * declarative refs (`ref="sheet"` / `ref="close[]"` in the liquid) + super
 * lifecycle. The TRIGGER-INTERCEPTION (discovering Horizon's external hamburger
 * via document queries + retry + MutationObserver) stays imperative — it targets
 * an EXTERNAL element, not a child ref. All open/close/scroll-lock/aria/focus
 * logic preserved byte-for-byte. Added a disconnectedCallback teardown of the
 * trigger-discovery MutationObserver (latent leak the raw version missed).
 * L-OATH-S48-MOBILE-MENU-HARDEN.
 *
 * Trigger discovery (priority): [data-oath-mobile-menu-trigger] → Horizon
 * [data-action="open-menu-drawer"] → [aria-controls*="menu-drawer"] →
 * summary.header__icon--menu → visible aria-label*=menu. Intercepts click
 * (preventDefault + open this overlay), mirrors aria-controls/expanded.
 */

import { Component } from '@theme/component';

class OathMobileMenu extends Component {
  constructor() {
    super();
    this._isOpen = false;
    this._savedScrollY = 0;
    this._onDocKey = this._onDocKey.bind(this);
    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onCloseClick = this._onCloseClick.bind(this);
    this._onBackdropClick = this._onBackdropClick.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    // Own-DOM handles via Component refs (ref="sheet" / ref="close[]" in the liquid).
    this._sheet = this.refs.sheet;
    this._closeBtns = this.refs.close || [];
    this._wireTriggers();

    this._closeBtns.forEach((b) => b.addEventListener('click', this._onCloseClick));
    this.addEventListener('click', this._onBackdropClick);
    document.addEventListener('keydown', this._onDocKey);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._closeBtns?.forEach((b) => b.removeEventListener('click', this._onCloseClick));
    this.removeEventListener('click', this._onBackdropClick);
    document.removeEventListener('keydown', this._onDocKey);
    if (this._trigger) {
      this._trigger.removeEventListener('click', this._onTriggerClick, true);
    }
    // S48 hardening: tear down the trigger-discovery observer if it never bound.
    if (this._mo) this._mo.disconnect();
  }

  _wireTriggers() {
    const matchAriaIncludes = (this.dataset.triggerAriaLabelIncludes || 'menu').toLowerCase();

    const findTrigger = () => {
      // Priority 1: explicit opt-in
      let trigger = document.querySelector('[data-oath-mobile-menu-trigger]');
      // Priority 2: Horizon's data-action variant
      if (!trigger) trigger = document.querySelector('[data-action="open-menu-drawer"]');
      // Priority 3: aria-controls variant
      if (!trigger) trigger = document.querySelector('button[aria-controls*="menu-drawer"], summary[aria-controls*="menu-drawer"]');
      // Priority 4: Horizon's actual hamburger pattern — <summary> with header__icon--menu class
      if (!trigger) trigger = document.querySelector('summary.header__icon--menu, .header__icon--menu');
      // Priority 5: aria-label text-match (visible only, skipping drawer-internal clones)
      if (!trigger) {
        const candidates = document.querySelectorAll('button, summary, a');
        for (const el of candidates) {
          if (el.closest('[class*="menu-drawer__"], dialog')) continue;
          if (el.offsetParent === null) continue;
          const label = (el.getAttribute('aria-label') || '').toLowerCase();
          if (label && label.includes(matchAriaIncludes)) { trigger = el; break; }
        }
      }
      return trigger;
    };

    const bind = (trigger) => {
      if (this._trigger) return;  // already bound
      this._trigger = trigger;
      // Capture phase so we run BEFORE Horizon's own drawer-open handler.
      trigger.addEventListener('click', this._onTriggerClick, true);
      trigger.setAttribute('aria-controls', this.id);
      trigger.setAttribute('aria-expanded', 'false');
    };

    // 1) Immediate try
    let t = findTrigger();
    if (t) { bind(t); return; }

    // 2) Extended retry budget (10s) — covers Horizon's lazy-loaded WCs
    let attempts = 0;
    const tryBind = () => {
      if (this._trigger) return;
      const tr = findTrigger();
      if (tr) { bind(tr); return; }
      attempts += 1;
      if (attempts < 50) setTimeout(tryBind, 200);
    };
    setTimeout(tryBind, 200);

    // 3) MutationObserver backstop — fires the instant Horizon adds the trigger
    // node to DOM. Disconnects on bind.
    this._mo = new MutationObserver(() => {
      if (this._trigger) {
        this._mo.disconnect();
        return;
      }
      const tr = findTrigger();
      if (tr) {
        bind(tr);
        this._mo.disconnect();
      }
    });
    this._mo.observe(document.body, { childList: true, subtree: true });

    // 4) Self-disconnect after 15s as a hard cap (in case Horizon never renders)
    setTimeout(() => {
      if (this._mo) this._mo.disconnect();
    }, 15000);
  }

  open() {
    if (this._isOpen) return;
    this._isOpen = true;
    this._savedScrollY = window.scrollY || document.documentElement.scrollTop || 0;
    this.removeAttribute('hidden');
    // Force reflow so the next class change can transition
    void this.offsetWidth;
    this.classList.add('is-open');
    document.documentElement.classList.add('oath-mobile-menu-open');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._savedScrollY}px`;
    document.body.style.width = '100%';
    if (this._trigger) this._trigger.setAttribute('aria-expanded', 'true');
    // Focus the X close for keyboard users
    const closeBtn = this._closeBtns?.[0];
    if (closeBtn) closeBtn.focus();
    this.dispatchEvent(new CustomEvent('oath:mobile-menu:open', { bubbles: true }));
  }

  close() {
    if (!this._isOpen) return;
    this._isOpen = false;
    this.classList.remove('is-open');
    // Wait for CSS transition (~220ms) before hiding via attribute
    const HIDE_DELAY = 240;
    setTimeout(() => {
      if (!this._isOpen) this.setAttribute('hidden', '');
    }, HIDE_DELAY);
    document.documentElement.classList.remove('oath-mobile-menu-open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this._savedScrollY);
    if (this._trigger) {
      this._trigger.setAttribute('aria-expanded', 'false');
      this._trigger.focus();
    }
    this.dispatchEvent(new CustomEvent('oath:mobile-menu:close', { bubbles: true }));
  }

  _onTriggerClick(e) {
    // Block Horizon's own drawer-open behavior; we own the menu now.
    e.preventDefault();
    e.stopImmediatePropagation();
    this.open();
  }

  _onCloseClick(e) {
    e.preventDefault();
    this.close();
  }

  _onBackdropClick(e) {
    if (!this._isOpen) return;
    // Click directly on the host (the backdrop area outside the sheet) closes.
    if (e.target === this) this.close();
  }

  _onDocKey(e) {
    if (e.key === 'Escape' && this._isOpen) {
      e.stopPropagation();
      this.close();
    }
  }
}

if (!customElements.get('oath-mobile-menu')) {
  customElements.define('oath-mobile-menu', OathMobileMenu);
}
